/**
 * Agent Orchestration & Tracking System
 * Coordinates agents throughout campaign workflow with intelligent recommendations
 */

import React, { useState, useEffect } from 'react';
import { 
  Zap, Users, MessageSquare, Brain, Target, CheckCircle,
  AlertCircle, Clock, ArrowRight, Play, Pause, RotateCcw,
  Eye, Settings, TrendingUp, Network, Heart, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { EmergencyHelpButton } from '../emma/EmergencyHelpButton';
import { EmmaChat } from '../emma/EmmaChat';

// Agent types and their workflow roles
export type AgentType = 
  | 'strategy' // Compass, Council, JVP
  | 'research' // Atlas, Brain, Riley
  | 'content' // Casey, Aria, Landing Page, Brief
  | 'brand' // Nova, Harmony, Adam
  | 'quality' // Rubric Evaluator, Trust Guardian
  | 'orchestration' // Emma, Symphony
  | 'specialized'; // CTA, Proof Point, etc.

export type AgentStatus = 
  | 'idle'
  | 'waiting'
  | 'running'
  | 'completed'
  | 'blocked'
  | 'error'
  | 'reviewing';

export type CampaignPhase = 
  | 'setup'
  | 'knowledge-ingestion'
  | 'stakeholder-mapping'
  | 'jvp-synthesis'
  | 'content-orchestration'
  | 'quality-assurance'
  | 'deployment';

// Agent configuration and activity tracking
interface Agent {
  id: string;
  name: string;
  type: AgentType;
  description: string;
  capabilities: string[];
  status: AgentStatus;
  currentTask?: string;
  progress: number; // 0-100
  priority: 'low' | 'medium' | 'high' | 'critical';
  
  // Workflow integration
  activePhases: CampaignPhase[];
  dependencies: string[]; // other agent IDs
  dependents: string[]; // agents waiting for this one
  
  // Performance metrics
  lastActive: Date;
  completedTasks: number;
  avgCompletionTime: number; // minutes
  successRate: number; // 0-1
  
  // Current activity
  startTime?: Date;
  estimatedCompletion?: Date;
  outputs: AgentOutput[];
  
  // User interaction
  needsReview: boolean;
  pendingDecisions: Decision[];
  recommendations: Recommendation[];
}

interface AgentOutput {
  id: string;
  type: 'content' | 'analysis' | 'recommendation' | 'data';
  title: string;
  content: React.ReactNode;
  quality_score: number;
  timestamp: Date;
  approved: boolean;
  feedback?: string;
}

interface Decision {
  id: string;
  title: string;
  description: string;
  options: DecisionOption[];
  priority: 'low' | 'medium' | 'high' | 'critical';
  deadline?: Date;
  context: string;
}

interface DecisionOption {
  id: string;
  label: string;
  description: string;
  impact: string;
  risk: 'low' | 'medium' | 'high';
}

interface Recommendation {
  id: string;
  title: string;
  description: string;
  action: string;
  reasoning: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: 'optimization' | 'quality' | 'efficiency' | 'risk';
  estimatedImpact: string;
}

// Workflow orchestration state
interface WorkflowState {
  currentPhase: CampaignPhase;
  phaseProgress: number; // 0-100
  activeAgents: string[];
  blockedAgents: string[];
  completedMilestones: string[];
  nextMilestones: string[];
  criticalPath: string[]; // agent IDs in order
  estimatedCompletion: Date;
}

// Emma's coordination intelligence
interface EmmaCoordination {
  activeCoordination: string[];
  workflowOptimizations: WorkflowOptimization[];
  agentConflicts: AgentConflict[];
  efficiencyMetrics: EfficiencyMetrics;
  nextActions: NextAction[];
}

interface WorkflowOptimization {
  id: string;
  type: 'parallelization' | 'sequencing' | 'resource-allocation' | 'bottleneck-resolution';
  description: string;
  impact: string;
  implementation: string;
  effort: 'low' | 'medium' | 'high';
}

interface AgentConflict {
  id: string;
  agentIds: string[];
  type: 'dependency-loop' | 'resource-contention' | 'output-conflict' | 'timing-conflict';
  description: string;
  resolution: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface EfficiencyMetrics {
  overallProgress: number;
  parallelizationScore: number;
  bottleneckSeverity: number;
  resourceUtilization: number;
  estimatedTimeToCompletion: number; // hours
}

interface NextAction {
  id: string;
  title: string;
  description: string;
  agent: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  estimatedDuration: number; // minutes
  blockers: string[];
}

// Component props
interface AgentOrchestrationPanelProps {
  campaignId: string;
  currentPhase: CampaignPhase;
  onPhaseChange?: (phase: CampaignPhase) => void;
  onAgentAction?: (agentId: string, action: string) => void;
}

export const AgentOrchestrationPanel: React.FC<AgentOrchestrationPanelProps> = ({
  campaignId,
  currentPhase,
  onPhaseChange,
  onAgentAction
}) => {
  // State management
  const [agents, setAgents] = useState<Agent[]>([]);
  const [workflowState, setWorkflowState] = useState<WorkflowState>({
    currentPhase,
    phaseProgress: 0,
    activeAgents: [],
    blockedAgents: [],
    completedMilestones: [],
    nextMilestones: [],
    criticalPath: [],
    estimatedCompletion: new Date()
  });
  const [emmaCoordination, setEmmaCoordination] = useState<EmmaCoordination>({
    activeCoordination: [],
    workflowOptimizations: [],
    agentConflicts: [],
    efficiencyMetrics: {
      overallProgress: 0,
      parallelizationScore: 0,
      bottleneckSeverity: 0,
      resourceUtilization: 0,
      estimatedTimeToCompletion: 0
    },
    nextActions: []
  });
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [viewMode, setViewMode] = useState<'overview' | 'workflow' | 'emma-coordination'>('overview');

  // Load agent configurations
  useEffect(() => {
    initializeAgents();
    const interval = setInterval(updateAgentStatus, 3000);
    return () => clearInterval(interval);
  }, [campaignId, currentPhase]);

  const initializeAgents = () => {
    const mockAgents: Agent[] = [
      // Emma Meta-Orchestrator
      {
        id: 'emma',
        name: 'Emma Meta-Orchestrator',
        type: 'orchestration',
        description: 'Meta-intelligence agent coordinating all campaign activities',
        capabilities: ['strategic-coordination', 'workflow-optimization', 'agent-orchestration', 'decision-support'],
        status: 'running',
        currentTask: 'Coordinating stakeholder mapping phase',
        progress: 75,
        priority: 'critical',
        activePhases: ['setup', 'stakeholder-mapping', 'jvp-synthesis', 'content-orchestration'],
        dependencies: [],
        dependents: ['compass', 'council', 'symphony'],
        lastActive: new Date(),
        completedTasks: 12,
        avgCompletionTime: 45,
        successRate: 0.95,
        startTime: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
        estimatedCompletion: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes from now
        outputs: [],
        needsReview: false,
        pendingDecisions: [],
        recommendations: [
          {
            id: 'rec_1',
            title: 'Parallel JVP Development',
            description: 'Begin JVP synthesis while stakeholder mapping completes',
            action: 'Launch JVP Synthesizer with preliminary stakeholder data',
            reasoning: 'Can reduce overall timeline by 20% with acceptable risk',
            priority: 'high',
            category: 'efficiency',
            estimatedImpact: '2-3 hours time savings'
          }
        ]
      },
      
      // Strategic Agents
      {
        id: 'compass',
        name: 'Compass Strategic Messaging',
        type: 'strategy',
        description: 'Strategic messaging framework and positioning agent',
        capabilities: ['message-framework', 'positioning', 'competitive-analysis'],
        status: 'completed',
        progress: 100,
        priority: 'high',
        activePhases: ['setup', 'jvp-synthesis'],
        dependencies: [],
        dependents: ['council', 'jvp-synthesizer'],
        lastActive: new Date(Date.now() - 10 * 60 * 1000),
        completedTasks: 8,
        avgCompletionTime: 35,
        successRate: 0.92,
        outputs: [
          {
            id: 'compass_output_1',
            type: 'analysis',
            title: 'Strategic Messaging Framework',
            content: 'Core messaging pillars and competitive positioning',
            quality_score: 94,
            timestamp: new Date(Date.now() - 10 * 60 * 1000),
            approved: true
          }
        ],
        needsReview: false,
        pendingDecisions: [],
        recommendations: []
      },
      
      {
        id: 'council',
        name: 'Council Committee Intelligence',
        type: 'strategy',
        description: 'Buying committee mapping and stakeholder analysis',
        capabilities: ['stakeholder-mapping', 'influence-analysis', 'committee-dynamics'],
        status: 'running',
        currentTask: 'Mapping CFO buying committee stakeholders',
        progress: 65,
        priority: 'critical',
        activePhases: ['stakeholder-mapping'],
        dependencies: ['compass'],
        dependents: ['symphony', 'jvp-synthesizer'],
        lastActive: new Date(),
        completedTasks: 5,
        avgCompletionTime: 55,
        successRate: 0.88,
        startTime: new Date(Date.now() - 45 * 60 * 1000),
        estimatedCompletion: new Date(Date.now() + 25 * 60 * 1000),
        outputs: [],
        needsReview: true,
        pendingDecisions: [
          {
            id: 'council_decision_1',
            title: 'Stakeholder Influence Weighting',
            description: 'Confirm influence scores for key stakeholders',
            options: [
              {
                id: 'opt_1',
                label: 'CFO Primary (90%)',
                description: 'CFO has primary decision authority',
                impact: 'Focus messaging on financial benefits',
                risk: 'low'
              },
              {
                id: 'opt_2',
                label: 'Committee Balanced (70%)',
                description: 'Balanced influence across committee',
                impact: 'Multi-persona messaging approach',
                risk: 'medium'
              }
            ],
            priority: 'high',
            deadline: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours
            context: 'Manufacturing CFO committee analysis shows mixed influence patterns'
          }
        ],
        recommendations: []
      },
      
      // Research Agents
      {
        id: 'atlas',
        name: 'Atlas Knowledge Graph',
        type: 'research',
        description: 'Knowledge graph construction and entity relationship mapping',
        capabilities: ['knowledge-extraction', 'entity-mapping', 'relationship-analysis'],
        status: 'waiting',
        priority: 'medium',
        activePhases: ['knowledge-ingestion'],
        dependencies: [],
        dependents: ['brain', 'riley'],
        lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000),
        completedTasks: 15,
        avgCompletionTime: 25,
        successRate: 0.96,
        outputs: [],
        needsReview: false,
        pendingDecisions: [],
        recommendations: [
          {
            id: 'atlas_rec_1',
            title: 'Additional Asset Upload',
            description: 'Missing competitive analysis documents for complete knowledge graph',
            action: 'Upload competitive intelligence reports',
            reasoning: 'Knowledge graph is 85% complete, competitive data would improve JVP quality',
            priority: 'medium',
            category: 'quality',
            estimatedImpact: '15% improvement in competitive positioning'
          }
        ],
        progress: 0
      },
      
      // Content Generation Agents
      {
        id: 'casey',
        name: 'Casey Email Generation',
        type: 'content',
        description: 'Persona-targeted email campaign generation',
        capabilities: ['email-generation', 'persona-targeting', 'sequence-planning'],
        status: 'idle',
        priority: 'medium',
        activePhases: ['content-orchestration'],
        dependencies: ['council', 'jvp-synthesizer'],
        dependents: [],
        lastActive: new Date(Date.now() - 4 * 60 * 60 * 1000),
        completedTasks: 28,
        avgCompletionTime: 20,
        successRate: 0.91,
        outputs: [],
        needsReview: false,
        pendingDecisions: [],
        recommendations: [],
        progress: 0
      },
      
      // JVP Synthesizer
      {
        id: 'jvp-synthesizer',
        name: 'JVP Synthesizer',
        type: 'strategy',
        description: 'Joint value proposition synthesis and alignment',
        capabilities: ['value-synthesis', 'stakeholder-alignment', 'consensus-building'],
        status: 'waiting',
        priority: 'critical',
        activePhases: ['jvp-synthesis'],
        dependencies: ['compass', 'council'],
        dependents: ['casey', 'aria', 'landing-page-gen'],
        lastActive: new Date(Date.now() - 1 * 60 * 60 * 1000),
        completedTasks: 6,
        avgCompletionTime: 65,
        successRate: 0.89,
        outputs: [],
        needsReview: false,
        pendingDecisions: [],
        recommendations: [],
        progress: 0
      }
    ];
    
    setAgents(mockAgents);
    updateWorkflowState(mockAgents);
    updateEmmaCoordination(mockAgents);
  };

  const updateAgentStatus = () => {
    setAgents(prev => prev.map(agent => {
      if (agent.status === 'running' && agent.progress < 100) {
        const progressIncrease = Math.random() * 5;
        const newProgress = Math.min(agent.progress + progressIncrease, 100);
        
        if (newProgress >= 100) {
          return { ...agent, progress: 100, status: 'completed' as AgentStatus };
        }
        
        return { ...agent, progress: newProgress };
      }
      return agent;
    }));
  };

  const updateWorkflowState = (agentList: Agent[]) => {
    const activeAgents = agentList.filter(a => a.status === 'running').map(a => a.id);
    const blockedAgents = agentList.filter(a => a.status === 'blocked' || a.status === 'waiting').map(a => a.id);
    
    setWorkflowState(prev => ({
      ...prev,
      activeAgents,
      blockedAgents,
      phaseProgress: calculatePhaseProgress(agentList, currentPhase)
    }));
  };

  const updateEmmaCoordination = (agentList: Agent[]) => {
    const optimizations: WorkflowOptimization[] = [
      {
        id: 'opt_1',
        type: 'parallelization',
        description: 'Run JVP synthesis parallel to final stakeholder mapping',
        impact: '25% time reduction',
        implementation: 'Use preliminary stakeholder data for JVP development',
        effort: 'low'
      }
    ];
    
    const nextActions: NextAction[] = agentList
      .filter(agent => agent.status === 'waiting' && agent.dependencies.every(dep => 
        agentList.find(a => a.id === dep)?.status === 'completed'
      ))
      .map(agent => ({
        id: `action_${agent.id}`,
        title: `Launch ${agent.name}`,
        description: `Dependencies satisfied, ready to begin ${agent.currentTask || 'next task'}`,
        agent: agent.id,
        priority: agent.priority,
        estimatedDuration: agent.avgCompletionTime,
        blockers: []
      }));
    
    setEmmaCoordination({
      activeCoordination: agentList.filter(a => a.status === 'running').map(a => a.id),
      workflowOptimizations: optimizations,
      agentConflicts: [],
      efficiencyMetrics: {
        overallProgress: agentList.reduce((sum, agent) => sum + agent.progress, 0) / agentList.length,
        parallelizationScore: 0.75,
        bottleneckSeverity: 0.3,
        resourceUtilization: 0.68,
        estimatedTimeToCompletion: 3.5
      },
      nextActions
    });
  };

  const calculatePhaseProgress = (agentList: Agent[], phase: CampaignPhase): number => {
    const phaseAgents = agentList.filter(agent => agent.activePhases.includes(phase));
    if (phaseAgents.length === 0) return 0;
    
    return phaseAgents.reduce((sum, agent) => sum + agent.progress, 0) / phaseAgents.length;
  };

  const getAgentTypeIcon = (type: AgentType) => {
    switch (type) {
      case 'orchestration':
        return <Brain className="h-5 w-5 text-purple-600" />;
      case 'strategy':
        return <Target className="h-5 w-5 text-blue-600" />;
      case 'research':
        return <Network className="h-5 w-5 text-green-600" />;
      case 'content':
        return <MessageSquare className="h-5 w-5 text-orange-600" />;
      case 'brand':
        return <Heart className="h-5 w-5 text-red-600" />;
      case 'quality':
        return <CheckCircle className="h-5 w-5 text-indigo-600" />;
      default:
        return <Zap className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusIcon = (status: AgentStatus) => {
    switch (status) {
      case 'running':
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'blocked':
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'waiting':
        return <Clock className="h-4 w-4 text-gray-400" />;
      default:
        return <div className="h-4 w-4 rounded-full bg-gray-300" />;
    }
  };

  const renderEmmaCoordination = () => (
    <div className="space-y-6">
      {/* Emma Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Brain className="h-6 w-6 text-purple-600" />
            <span>Emma Meta-Orchestrator</span>
            <Badge variant="default">Active</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{emmaCoordination.activeCoordination.length}</div>
              <div className="text-sm text-gray-600">Active Agents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{Math.round(emmaCoordination.efficiencyMetrics.overallProgress)}%</div>
              <div className="text-sm text-gray-600">Overall Progress</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{emmaCoordination.efficiencyMetrics.estimatedTimeToCompletion}h</div>
              <div className="text-sm text-gray-600">Est. Completion</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{emmaCoordination.workflowOptimizations.length}</div>
              <div className="text-sm text-gray-600">Optimizations</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Emma's Recommended Next Actions</CardTitle>
        </CardHeader>
        <CardContent>
          {emmaCoordination.nextActions.length === 0 ? (
            <p className="text-gray-600 text-center py-4">All agents are properly sequenced. No immediate actions required.</p>
          ) : (
            <div className="space-y-3">
              {emmaCoordination.nextActions.map(action => (
                <div key={action.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{action.title}</div>
                    <div className="text-sm text-gray-600">{action.description}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Estimated duration: {action.estimatedDuration} minutes
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={action.priority === 'critical' ? 'destructive' : action.priority === 'high' ? 'default' : 'secondary'}>
                      {action.priority}
                    </Badge>
                    <Button size="small" onClick={() => onAgentAction?.(action.agent, 'launch')}>
                      <Play className="h-4 w-4 mr-1" />
                      Launch
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workflow Optimizations */}
      <Card>
        <CardHeader>
          <CardTitle>Workflow Optimizations</CardTitle>
        </CardHeader>
        <CardContent>
          {emmaCoordination.workflowOptimizations.map(optimization => (
            <div key={optimization.id} className="p-4 border border-gray-200 rounded-lg mb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium text-blue-700">{optimization.description}</div>
                  <div className="text-sm text-gray-600 mt-1">Impact: {optimization.impact}</div>
                  <div className="text-sm text-gray-600">Implementation: {optimization.implementation}</div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">{optimization.effort} effort</Badge>
                  <Button size="small" variant="primary">Apply</Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  const renderAgentGrid = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {agents.map(agent => (
        <Card key={agent.id} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedAgent(agent)}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                {getAgentTypeIcon(agent.type)}
                <div>
                  <h4 className="font-medium">{agent.name}</h4>
                  <p className="text-sm text-gray-500">{agent.type}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {getStatusIcon(agent.status)}
                <Badge variant={
                  agent.status === 'completed' ? 'default' :
                  agent.status === 'running' ? 'secondary' :
                  agent.status === 'error' || agent.status === 'blocked' ? 'destructive' :
                  'secondary'
                }>
                  {agent.status}
                </Badge>
              </div>
            </div>
            
            {agent.currentTask && (
              <div className="mb-3">
                <div className="text-sm text-gray-600 mb-1">{agent.currentTask}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${agent.progress}%` }}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-1">{Math.round(agent.progress)}% complete</div>
              </div>
            )}
            
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Priority</span>
              <Badge variant={agent.priority === 'critical' ? 'destructive' : agent.priority === 'high' ? 'default' : 'secondary'}>
                {agent.priority}
              </Badge>
            </div>
            
            {agent.needsReview && (
              <div className="mt-2">
                <Badge variant="destructive" className="text-xs">
                  Needs Review
                </Badge>
              </div>
            )}
            
            {agent.recommendations.length > 0 && (
              <div className="mt-2">
                <Badge variant="secondary" className="text-xs">
                  {agent.recommendations.length} recommendation{agent.recommendations.length > 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const [showEmmaHelp, setShowEmmaHelp] = useState(false);

  const handleEmmaHelp = () => {
    setShowEmmaHelp(true);
  };

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agent Orchestration</h2>
          <p className="text-gray-600">Monitor and coordinate AI agent activities throughout campaign workflow</p>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="overview">Agent Overview</option>
            <option value="workflow">Workflow View</option>
            <option value="emma-coordination">Emma Coordination</option>
          </select>
          <Button variant="primary" size="small">
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      {/* Workflow Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Current Phase: {currentPhase.replace('-', ' ').toUpperCase()}</h3>
            <div className="text-sm text-gray-600">{Math.round(workflowState.phaseProgress)}% Complete</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${workflowState.phaseProgress}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>{workflowState.activeAgents.length} active agents</span>
            <span>{workflowState.blockedAgents.length} waiting/blocked</span>
          </div>
        </CardContent>
      </Card>

      {/* Content based on view mode */}
      {viewMode === 'overview' && renderAgentGrid()}
      {viewMode === 'emma-coordination' && renderEmmaCoordination()}
      {viewMode === 'workflow' && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🔄</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Workflow Visualization</h3>
          <p className="text-gray-600">Interactive workflow diagram will be displayed here</p>
        </div>
      )}

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  {getAgentTypeIcon(selectedAgent.type)}
                  <span>{selectedAgent.name}</span>
                  <Badge variant={selectedAgent.status === 'completed' ? 'default' : 'secondary'}>
                    {selectedAgent.status}
                  </Badge>
                </CardTitle>
                <Button variant="ghost" size="small" onClick={() => setSelectedAgent(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-gray-600">{selectedAgent.description}</p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Capabilities</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedAgent.capabilities.map(capability => (
                      <Badge key={capability} variant="secondary">
                        {capability.replace('-', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {selectedAgent.pendingDecisions.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Pending Decisions</h4>
                    <div className="space-y-3">
                      {selectedAgent.pendingDecisions.map(decision => (
                        <div key={decision.id} className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                          <div className="font-medium text-orange-800">{decision.title}</div>
                          <div className="text-sm text-orange-700 mt-1">{decision.description}</div>
                          <div className="mt-3 space-y-2">
                            {decision.options.map(option => (
                              <div key={option.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div>
                                  <div className="font-medium text-sm">{option.label}</div>
                                  <div className="text-xs text-gray-600">{option.description}</div>
                                </div>
                                <Button size="small" variant="primary">Select</Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedAgent.recommendations.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Recommendations</h4>
                    <div className="space-y-3">
                      {selectedAgent.recommendations.map(rec => (
                        <div key={rec.id} className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                          <div className="font-medium text-blue-800">{rec.title}</div>
                          <div className="text-sm text-blue-700 mt-1">{rec.description}</div>
                          <div className="text-xs text-blue-600 mt-2">
                            Impact: {rec.estimatedImpact} • Priority: {rec.priority}
                          </div>
                          <div className="mt-2">
                            <Button size="small" variant="primary">Apply</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* EPIC CITE-RECOVERY-001: Emma escape hatch */}
      <div className="fixed bottom-6 right-6 z-50">
        <EmergencyHelpButton 
          onClick={handleEmmaHelp}
          isVisible={!showEmmaHelp}
        />
      </div>
      
      {/* Emma Chat Modal */}
      {showEmmaHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-3/4 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Emma - Your Agent Orchestration Assistant</h2>
              <button
                onClick={() => setShowEmmaHelp(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <EmmaChat 
                initialContext={{
                  currentPage: 'agent-orchestration',
                  activeAgents: workflowState.activeAgents.length,
                  currentPhase: currentPhase
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentOrchestrationPanel;
